import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { TokenStatus } from 'gql/index';
import { TokenPurposeContextGraphQl } from 'gql/index';
import { AccessLinkExpiredAlert } from './components/AccessLinkExpiredAlert';

interface DocumentSignatureContentProps {
  context: TokenPurposeContextGraphQl;
  onAction: () => void;
  onRenew: () => void;
  isRenewing: boolean;
}

export const DocumentSignatureContent: React.FC<DocumentSignatureContentProps> = ({
  context,
  onAction,
  onRenew,
  isRenewing,
}) => {
  const { formatMessage } = useIntl();

  if (context.status === TokenStatus.Completed) {
    return (
      <Stack spacing={3} p={3}>
        <Alert severity="success">
          {formatMessage({ id: 'This document has already been signed.' })}
        </Alert>
        {context.initiatorName && (
          <Typography variant="body1">
            {formatMessage({ id: 'Requested by {name}' }, { name: context.initiatorName })}
          </Typography>
        )}
        {context.resourceName && (
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: 'Document: {name}' }, { name: context.resourceName })}
          </Typography>
        )}
      </Stack>
    );
  }

  if (context.status === TokenStatus.Expired) {
    return (
      <Stack spacing={3} p={3}>
        <AccessLinkExpiredAlert message={context.errorMessage} />
        {context.initiatorName && (
          <Typography variant="body1">
            {formatMessage(
              { id: '{name} requested your signature' },
              { name: context.initiatorName }
            )}
          </Typography>
        )}
        {context.resourceName && (
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: 'Document: {name}' }, { name: context.resourceName })}
          </Typography>
        )}
        {context.customMessage && (
          <Typography variant="body2">
            {context.customMessage}
          </Typography>
        )}
        {context.isRenewable && (
          <Button
            variant="contained"
            onClick={onRenew}
            disabled={isRenewing}
            fullWidth
          >
            {isRenewing ? (
              <CircularProgress size={24} />
            ) : (
              formatMessage({ id: 'Request New Access Link' })
            )}
          </Button>
        )}
      </Stack>
    );
  }

  if (context.status === TokenStatus.Invalid) {
    return (
      <Stack spacing={3} p={3}>
        <Alert severity="error">
          {context.errorMessage || formatMessage({ id: 'This access link is no longer valid.' })}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} p={3}>
      {context.initiatorName && (
        <Typography variant="body1" color="text.primary">
          {formatMessage(
            { id: '{name} has requested your signature' },
            { name: context.initiatorName }
          )}
        </Typography>
      )}
      {context.resourceName && (
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {context.resourceName}
        </Typography>
      )}
      {context.customMessage && (
        <Typography variant="body2" color="text.secondary">
          {context.customMessage}
        </Typography>
      )}
      <Button
        variant="contained"
        onClick={onAction}
        fullWidth
        size="large"
      >
        {formatMessage({ id: 'Review and Sign' })}
      </Button>
    </Stack>
  );
};
